import { createContext, useContext, useState, useEffect } from 'react';
import { googleLogout } from '@react-oauth/google';
import { http } from '@/lib/http.js';

/**
 * AuthContext — Google OAuth.
 *
 * El login se dispara desde LoginScreen via `loginWithGoogle(credentialResponse)`
 * que recibe el objeto de @react-oauth/google's useGoogleLogin / GoogleLogin.
 *
 * Solo se permite acceso a cuentas @xperience.group.
 *
 * El token JWT de Google se guarda en localStorage para que http.js
 * lo adjunte automáticamente en el header Authorization.
 * 
 */

const AuthContext = createContext(null);

const STORAGE_KEY_USER  = 'xp_auth_user';
const STORAGE_KEY_TOKEN = 'xp_auth_token';


export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Restaurar sesión al cargar
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_USER);
      if (stored) setUser(JSON.parse(stored));
    } catch {
      localStorage.removeItem(STORAGE_KEY_USER);
      localStorage.removeItem(STORAGE_KEY_TOKEN);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Llamar desde LoginScreen con el objeto que devuelve
   * el callback `onSuccess` del componente <GoogleLogin />.
   *
   * credentialResponse = { credential: "<id_token JWT>" }
   */
  
  const loginWithGoogle = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) throw new Error('No se recibió token de Google.');

    const response = await http.post('/auth/google', { idToken });
    const { token, user: safeUser } = response;

    localStorage.setItem(STORAGE_KEY_USER,  JSON.stringify(safeUser));
    localStorage.setItem(STORAGE_KEY_TOKEN, token);

    setUser(safeUser);
    return safeUser;
  };

  const logout = () => {
    googleLogout();
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loginWithGoogle, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};